import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit, Search, X, Layers, Wand2, Loader2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const UnitBadge = ({ unit }) => (
  <span className="ml-1 px-1.5 py-0 rounded text-[11px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
    {unit || "―"}
  </span>
);

const SubBadge = ({ text }) =>
  text ? (
    <span className="ml-1 px-1.5 py-0 rounded text-[11px] font-bold bg-amber-100 text-amber-700 border border-amber-300">
      {text}
    </span>
  ) : null;

export default function UnitGroupsManagement() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]); // {id,name,unit,code,sub_display}

  // product search
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // suggestions
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSug, setLoadingSug] = useState(false);
  const [creatingSug, setCreatingSug] = useState(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/unit-groups`, getAuthHeaders());
      setGroups(res.data || []);
    } catch (e) {
      toast.error("グループの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(
          `${API}/products/paginated?search=${encodeURIComponent(q)}&page_size=25`,
          getAuthHeaders()
        );
        setResults(res.data?.items || []);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setSelected([]);
    setQuery("");
    setResults([]);
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setName(g.name || "");
    setSelected((g.products || []).map((p) => ({ id: p.id, name: p.name, unit: p.unit, code: p.code, sub_display: p.sub_display })));
    setQuery("");
    setResults([]);
    setDialogOpen(true);
  };

  const toggle = (p) => {
    const exists = selected.find((x) => x.id === p.id);
    if (exists) setSelected(selected.filter((x) => x.id !== p.id));
    else setSelected([...selected, { id: p.id, name: p.name, unit: p.unit, code: p.code, sub_display: p.sub_display }]);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("グループ名を入力してください");
    if (selected.length < 2) return toast.error("同じ商品の単位違いを2つ以上選んでください");
    const payload = { name: name.trim(), product_ids: selected.map((s) => s.id) };
    try {
      if (editing) {
        await axios.put(`${API}/admin/unit-groups/${editing.id}`, payload, getAuthHeaders());
        toast.success("グループを更新しました");
      } else {
        await axios.post(`${API}/admin/unit-groups`, payload, getAuthHeaders());
        toast.success("グループを作成しました");
      }
      setDialogOpen(false);
      fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "保存に失敗しました");
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`グループ「${g.name}」を解除しますか？（商品自体は消えません）`)) return;
    try {
      await axios.delete(`${API}/admin/unit-groups/${g.id}`, getAuthHeaders());
      toast.success("グループを解除しました");
      fetchGroups();
    } catch (e) {
      toast.error("解除に失敗しました");
    }
  };

  const loadSuggestions = async () => {
    setLoadingSug(true);
    try {
      const res = await axios.get(`${API}/admin/unit-groups/suggestions`, { ...getAuthHeaders(), timeout: 120000 });
      setSuggestions(res.data?.suggestions || []);
      toast.success(`${res.data?.count || 0} 件の候補が見つかりました`);
    } catch (e) {
      toast.error("候補の取得に失敗しました");
    } finally {
      setLoadingSug(false);
    }
  };

  const createFromSuggestion = async (s, idx) => {
    setCreatingSug(idx);
    try {
      await axios.post(
        `${API}/admin/unit-groups`,
        { name: s.suggested_name, product_ids: s.products.map((p) => p.id) },
        getAuthHeaders()
      );
      toast.success(`「${s.suggested_name}」をグループ化しました`);
      setSuggestions((prev) => prev.filter((_, i) => i !== idx));
      fetchGroups();
    } catch (e) {
      toast.error("作成に失敗しました");
    } finally {
      setCreatingSug(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="unit-groups-management">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-7 bg-emerald-600 rounded-full" />
            <h1 className="text-2xl font-bold text-slate-800">単位グループ管理</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            同じ商品の「単位違い（例：本 と kg）」を紐付けます。名前が違っても紐付けでき、紐付けた商品は辞書の有無に関係なく全顧客で単位を切り替えて発注できます。
          </p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="unit-group-new-btn">
          <Plus className="h-4 w-4 mr-1" />新しいグループ
        </Button>
      </div>

      {/* 自動サジェスト */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="unit-group-suggestions">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-violet-600" />
            <h2 className="text-base font-semibold text-slate-800">自動サジェスト</h2>
          </div>
          <Button onClick={loadSuggestions} disabled={loadingSug} variant="outline" size="sm" data-testid="load-suggestions-btn">
            {loadingSug ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1" />}
            候補を検出
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          名前（表記ゆれ含む）やデスクのふりがなが一致し、単位が異なる商品の組み合わせを提案します。内容を確認して「グループ化」を押してください。
        </p>
        {suggestions && (
          <div className="mt-3 space-y-2">
            {suggestions.length === 0 ? (
              <div className="text-sm text-slate-400 py-4 text-center">候補は見つかりませんでした（手動でグループ化してください）。</div>
            ) : (
              suggestions.map((s, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg p-2.5 bg-slate-50" data-testid={`suggestion-${idx}`}>
                  <div className="min-w-0">
                    <div className="text-xs text-violet-600 mb-0.5">{s.reason}</div>
                    <div className="text-sm text-slate-700 flex flex-wrap gap-x-2 gap-y-1 items-center">
                      {s.products.map((p) => (
                        <span key={p.id} className="inline-flex items-center">
                          {p.name}
                          <UnitBadge unit={p.unit} />
                          <SubBadge text={p.sub_display} />
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => createFromSuggestion(s, idx)} disabled={creatingSug === idx} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0" data-testid={`suggestion-create-${idx}`}>
                    {creatingSug === idx ? <Loader2 className="h-4 w-4 animate-spin" /> : "グループ化"}
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* 既存グループ一覧 */}
      {loading ? (
        <div className="text-slate-400 text-sm py-10 text-center">読み込み中...</div>
      ) : groups.length === 0 ? (
        <div className="text-slate-400 text-sm py-10 text-center border border-dashed border-slate-200 rounded-lg" data-testid="unit-groups-empty">
          まだグループがありません。「新しいグループ」または自動サジェストから作成してください。
        </div>
      ) : (
        <div className="grid gap-3">
          {groups.map((g) => (
            <div key={g.id} data-testid={`unit-group-${g.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex gap-4 items-start">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                <Layers className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-800">{g.name} <span className="text-xs text-slate-400 font-normal">（{g.product_count}商品）</span></h3>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  {(g.products || []).map((p) => (
                    <span key={p.id} className="text-sm text-slate-600 inline-flex items-center">
                      {p.name}
                      <UnitBadge unit={p.unit} />
                      <SubBadge text={p.sub_display} />
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => openEdit(g)} data-testid={`unit-group-edit-${g.id}`}>
                  <Edit className="h-4 w-4 text-slate-600" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(g)} data-testid={`unit-group-delete-${g.id}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="unit-group-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "グループを編集" : "新しい単位グループ"}</DialogTitle>
            <DialogDescription>同じ商品の単位違いを2つ以上選んで紐付けます。名前が違っても構いません。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>グループ名</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="例：バナナ" className="mt-1" data-testid="unit-group-name-input" lang="ja" />
            </div>
            <div>
              <Label>商品（2つ以上）</Label>
              {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="unit-group-selected">
                  {selected.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full pl-2 pr-1 py-0.5">
                      {p.name}<UnitBadge unit={p.unit} />
                      <button type="button" onClick={() => toggle(p)} className="hover:bg-emerald-200 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative mt-1.5">
                <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="商品名・コードで検索して追加" className="pl-8" data-testid="unit-group-product-search" lang="ja" />
              </div>
              {(searching || results.length > 0) && (
                <div className="mt-1 border border-slate-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-slate-100">
                  {searching && <div className="px-3 py-2 text-xs text-slate-400">検索中...</div>}
                  {results.map((p) => {
                    const sel = selected.find((x) => x.id === p.id);
                    return (
                      <button key={p.id} type="button" onClick={() => toggle(p)} data-testid={`unit-group-option-${p.id}`}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${sel ? "bg-emerald-50" : ""}`}>
                        <span className="truncate flex items-center">
                          {p.name}<UnitBadge unit={p.unit} /><SubBadge text={p.sub_display} />
                        </span>
                        {sel && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="unit-group-save-btn">保存</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
